import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  CONSENT_STORAGE_KEY,
  COOKIE_CONSENT_VERSION,
  ConsentCategories,
  ConsentCategory,
  ConsentState,
  CookieConsentContextValue,
} from '../types/cookieConsent';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildDefaultState(overrides?: Partial<ConsentCategories>): ConsentState {
  return {
    consentGiven: false,
    version: COOKIE_CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    categories: {
      necessary: true,
      analytics: false,
      functional: false,
      marketing: false,
      ...overrides,
    },
  };
}

function loadStoredConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed: ConsentState = JSON.parse(raw);
    // Re-prompt if the consent version has changed
    if (parsed.version !== COOKIE_CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function persistConsent(state: ConsentState): void {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage unavailable — fail silently
  }
}

// ─── Context ──────────────────────────────────────────────────────────────────

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [consentState, setConsentState] = useState<ConsentState | null>(null);
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);

  // Hydrate from storage on mount (client-only)
  useEffect(() => {
    const stored = loadStoredConsent();
    if (stored) {
      setConsentState(stored);
      setIsBannerVisible(false);
    } else {
      setIsBannerVisible(true);
    }
  }, []);

  const save = useCallback((next: ConsentState) => {
    persistConsent(next);
    setConsentState(next);
    setIsBannerVisible(false);
    setIsPreferencesOpen(false);
    // Emit a custom event so gated loaders can react
    window.dispatchEvent(new CustomEvent('lookbel:consent-updated', { detail: next }));
  }, []);

  const acceptAll = useCallback(() => {
    const state = buildDefaultState({
      necessary: true,
      analytics: true,
      functional: true,
      marketing: true,
    });
    save({ ...state, consentGiven: true });
  }, [save]);

  const rejectNonEssential = useCallback(() => {
    save({ ...buildDefaultState(), consentGiven: true });
  }, [save]);

  const savePreferences = useCallback(
    (categories: Omit<ConsentCategories, 'necessary'>) => {
      save({
        consentGiven: true,
        version: COOKIE_CONSENT_VERSION,
        timestamp: new Date().toISOString(),
        categories: { necessary: true, ...categories },
      });
    },
    [save]
  );

  const resetConsent = useCallback(() => {
    try {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
    } catch {
      // ignore
    }
    setConsentState(null);
    setIsBannerVisible(true);
    setIsPreferencesOpen(false);
  }, []);

  const hasConsent = useCallback(
    (category: ConsentCategory): boolean => {
      if (category === 'necessary') return true;
      return consentState?.categories?.[category] ?? false;
    },
    [consentState]
  );

  const openPreferences = useCallback(() => setIsPreferencesOpen(true), []);
  const closePreferences = useCallback(() => setIsPreferencesOpen(false), []);

  const value = useMemo<CookieConsentContextValue>(
    () => ({
      consentState,
      hasConsent,
      acceptAll,
      rejectNonEssential,
      savePreferences,
      resetConsent,
      openPreferences,
      isPreferencesOpen,
      closePreferences,
      isBannerVisible,
    }),
    [
      consentState,
      hasConsent,
      acceptAll,
      rejectNonEssential,
      savePreferences,
      resetConsent,
      openPreferences,
      isPreferencesOpen,
      closePreferences,
      isBannerVisible,
    ]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCookieConsent(): CookieConsentContextValue {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error('useCookieConsent must be used inside <CookieConsentProvider>');
  }
  return ctx;
}

export default CookieConsentContext;
